import { Mail, Phone, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FlagMA } from "../shared/FlagIcons";

// Renders a UI string, replacing the 🇲🇦 emoji with the SVG flag (emoji
// flags don't render on Windows — they fall back to plain "MA" text).
function withMoroccoFlag(value: string) {
  const [before, after] = value.split("🇲🇦");
  if (after === undefined) return value;
  return (
    <>
      {before}
      <FlagMA className="mx-0.5 align-[-2px]" />
      {after}
    </>
  );
}

// Displays branded contact details and social links using environment configuration.
export default function Footer() {
  const { t } = useTranslation();
  const lastUpdated = new Date(__LAST_UPDATED__).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  
  // Get contact info from environment variables
  const contactEmail = import.meta.env.VITE_CONTACT_EMAIL || 'DarZaytounBhalil@gmail.com';
  const contactPhoneCall = import.meta.env.VITE_CONTACT_PHONE_CALL || '+212770754238';
  const contactPhoneWhatsapp = import.meta.env.VITE_CONTACT_PHONE_WHATSAPP || '+212770780837';
  const contactAddress = import.meta.env.VITE_CONTACT_ADDRESS || 'Bhalil Village, Fez-Meknes Region, Morocco';
  const socialInstagram = import.meta.env.VITE_SOCIAL_INSTAGRAM || '#';
  const socialFacebook = import.meta.env.VITE_SOCIAL_FACEBOOK || '#';
  const socialWhatsapp = import.meta.env.VITE_SOCIAL_WHATSAPP || '#';
  const socialTiktok = import.meta.env.VITE_SOCIAL_TIKTOK || '#';
  return (
    <footer
      id="contact"
      className="relative bg-gradient-to-b from-[#8b5a2b] via-[#7a4d25] to-[#5f3b1b] dark:from-[#0a0300] dark:via-[#070200] dark:to-[#000000] text-white overflow-hidden transition-colors duration-500"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#f5e0bf]/10 via-transparent to-transparent"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div
          className="relative backdrop-blur-md sm:backdrop-blur-xl bg-white/15 rounded-xl sm:rounded-3xl p-5 sm:p-6 md:p-8 shadow-lg border border-white/20"
          style={{
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.05))",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
          }}
        >
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-10">
            <div>
              <div className="flex items-center space-x-3 mb-3">
                <img
                  src="/logo.webp"
                  alt="Dar Zaytoun Logo"
                  className="w-14 sm:w-16 h-14 sm:h-16 object-contain flex-shrink-0 filter brightness-110 drop-shadow-lg"
                />
                <div>
                  <h3 className="text-base sm:text-xl font-serif font-bold drop-shadow-md">
                    {t("header.siteName")}
                  </h3>
                  <p className="text-white/90 text-xs">
                    {t("header.location")}
                  </p>
                </div>
              </div>
              <p className="text-white/90 text-xs sm:text-sm leading-relaxed">
                {t("footer.description")}
              </p>
            </div>

            <div>
              <h4 className="text-base sm:text-lg font-serif font-bold mb-3 drop-shadow-md">
                {t("footer.contactUs")}
              </h4>
              <div className="space-y-2 sm:space-y-3">
                <a
                  href={`mailto:${contactEmail}`}
                  className="flex items-center text-white/90 hover:text-white hover:translate-x-1 transition-all text-xs sm:text-sm"
                >
                  <Mail size={16} className="mr-2 flex-shrink-0 min-w-[16px]" />
                  <span>{contactEmail}</span>
                </a>
                <div className="flex gap-3 flex-wrap">
                  <a
                    href={`tel:${contactPhoneCall}`}
                    className="flex items-center text-white/90 hover:text-white hover:translate-x-1 transition-all text-xs sm:text-sm"
                  >
                    <Phone
                      size={16}
                      className="mr-2 flex-shrink-0 min-w-[16px]"
                    />
                    <div className="flex flex-col">
                      <span>{contactPhoneCall}</span>
                      <span className="text-[10px] text-white/70">({t("footer.call", "Call")})</span>
                    </div>
                  </a>
                  <a
                    href={`https://wa.me/${contactPhoneWhatsapp.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-white/90 hover:text-white hover:translate-x-1 transition-all text-xs sm:text-sm"
                  >
                    <Phone
                      size={16}
                      className="mr-2 flex-shrink-0 min-w-[16px]"
                    />
                    <div className="flex flex-col">
                      <span>{contactPhoneWhatsapp}</span>
                      <span className="text-[10px] text-white/70">(WhatsApp)</span>
                    </div>
                  </a>
                </div>
                <div className="flex items-center text-white/90 hover:text-white hover:translate-x-1 transition-all text-xs sm:text-sm">
                  <MapPin
                    size={16}
                    className="mr-2 flex-shrink-0 min-w-[16px]"
                  />
                  <span>{contactAddress}</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-base sm:text-lg font-serif font-bold mb-3 drop-shadow-md">
                {t("footer.followUs")}
              </h4>
              <div className="flex space-x-3">
                {/* Instagram */}
                <a
                  href={socialInstagram}
                  className="w-9 h-9 backdrop-blur-md bg-white/20 hover:bg-white/30 hover:scale-110 rounded-full flex items-center justify-center transition-all duration-300 border border-white/30"
                >
                  <img
                    src="/icons/brands/instagram.svg"
                    alt="Instagram"
                    loading="lazy"
                    className="w-4 h-4"
                  />
                </a>

                {/* Facebook */}
                <a
                  href={socialFacebook}
                  className="w-9 h-9 backdrop-blur-md bg-white/20 hover:bg-white/30 hover:scale-110 rounded-full flex items-center justify-center transition-all duration-300 border border-white/30"
                >
                  <img
                    src="/icons/brands/facebook.svg"
                    alt="Facebook"
                    loading="lazy"
                    className="w-4 h-4"
                  />
                </a>

                {/* WhatsApp */}
                <a
                  href={socialWhatsapp}
                  className="w-9 h-9 backdrop-blur-md bg-white/20 hover:bg-white/30 hover:scale-110 rounded-full flex items-center justify-center transition-all duration-300 border border-white/30"
                >
                  <img
                    src="/icons/brands/whatsapp.svg"
                    alt="WhatsApp"
                    loading="lazy"
                    className="w-4 h-4"
                  />
                </a>

                {/* TikTok */}
                <a
                  href={socialTiktok}
                  className="w-9 h-9 backdrop-blur-md bg-white/20 hover:bg-white/30 hover:scale-110 rounded-full flex items-center justify-center transition-all duration-300 border border-white/30"
                >
                  <img
                    src="/icons/brands/tiktok.svg"
                    alt="TikTok"
                    loading="lazy"
                    className="w-4 h-4"
                  />
                </a>
              </div>
              <a
                href="/bio"
                className="mt-4 inline-flex text-xs text-white/90 underline decoration-white/50 underline-offset-4 transition-colors hover:text-white"
              >
                {t("footer.quickLinks", "Quick Links")}
              </a>
            </div>
          </div>

          <div className="relative border-t border-white/30 mt-6 pt-4 text-center text-white/80 text-[10px] sm:text-xs">
            <p>
              &copy; {new Date().getFullYear()} {t("header.siteName")}.{" "}
              {t("footer.copyright")}
              <span className="block mt-1">{t("footer.lastUpdated")}: {lastUpdated}</span>
            </p>
            <p className="mt-0.5 flex items-center justify-center gap-1 flex-wrap">
              <span>{withMoroccoFlag(t("footer.craftedWith"))}</span>
              <a
                href="https://www.instagram.com/Linkinskye"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-2 py-0.5 bg-[#8b6f47] hover:bg-[#9d7d52] rounded-md text-white font-semibold transition-all hover:scale-105"
              >
                @AmineSoukara
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
